// Filter groups for the bike catalog. Each group reads one value from a bike row; a bike passes
// a group when nothing is picked in it or its value is one of the picked options.
import seo from '../seo/seoConfig.json';
import { bikePrice } from '../utils';

const text = (value) => String(value || '');

export const categoryOf = (bike, seriesById) => seriesById[bike.serie_id]?.categoria_id || null;
export const categoryName = (bike, seriesById) => seo.categories[categoryOf(bike, seriesById)]?.name || '';

const PRICE_RANGES = [
    { value: 'hasta-1000', label: 'Hasta USD 1,000', below: 1000 },
    { value: '1000-3000', label: 'USD 1,000 – 3,000', below: 3000 },
    { value: '3000-5000', label: 'USD 3,000 – 5,000', below: 5000 },
    { value: '5000-7000', label: 'USD 5,000 – 7,000', below: 7000 },
    { value: 'desde-7000', label: 'Más de USD 7,000', below: Infinity }
];

// cuadro_material usually starts with the wheel size ('29/27.5" Alloy 6061'); some rows only have it in the tyres.
const wheelSize = (bike) => {
    const match = text(bike.cuadro_material).match(/^\s*([\d.]+(?:\/[\d.]+)?)\s*["“”]/)
        || text(bike.neumaticos).match(/([\d.]+)\s*["“”]/)
        || text(bike.tire_f).match(/\b(\d{2}(?:\.5)?)x\d/);
    return match ? match[1] : null;
};

const suspension = (bike) => {
    const shock = text(bike.shock || bike.amortiguador).trim();
    if ((shock && shock !== '-') || (bike.tipos_marco || []).some((t) => /full/i.test(t))) return 'doble';
    return bike.recorrido_mm > 0 ? 'delantera' : 'rigida';
};

export const FILTER_GROUPS = [
    {
        id: 'tipo',
        label: 'Tipo',
        value: (bike, ctx) => {
            const category = categoryOf(bike, ctx.seriesById);
            if (!category) return null;
            return category <= 4 ? 'electrica' : 'sin-motor';
        },
        options: [
            { value: 'electrica', label: 'Eléctricas' },
            { value: 'sin-motor', label: 'Análogas' }
        ]
    },
    {
        id: 'categoria',
        label: 'Categoría',
        value: (bike, ctx) => String(categoryOf(bike, ctx.seriesById) || '') || null,
        options: Object.entries(seo.categories).map(([id, category]) => ({ value: id, label: category.name }))
    },
    {
        id: 'precio',
        label: 'Precio',
        value: (bike) => {
            const price = bikePrice(bike);
            return price ? PRICE_RANGES.find((range) => price < range.below).value : null;
        },
        options: PRICE_RANGES
    },
    {
        id: 'rueda',
        label: 'Tamaño de rueda',
        value: wheelSize,
        // Options come from the bikes on the page, smallest wheel first
        sort: (a, b) => parseFloat(a) - parseFloat(b) || a.length - b.length,
        format: (size) => (size.includes('/') ? `Mixta ${size.replace('/', '" / ')}"` : `${size}"`)
    },
    {
        id: 'suspension',
        label: 'Suspensión',
        value: suspension,
        options: [
            { value: 'doble', label: 'Doble suspensión' },
            { value: 'delantera', label: 'Solo delantera' },
            { value: 'rigida', label: 'Rígida' }
        ]
    },
    {
        id: 'motor',
        label: 'Motor',
        value: (bike) => text(bike.motor_modelo).trim().split(/[\s,]+/)[0] || null
    },
    {
        id: 'cuadro',
        label: 'Material del cuadro',
        value: (bike) => {
            const frame = text(bike.cuadro_material);
            if (/carbon/i.test(frame)) return 'carbono';
            if (/alloy|alu/i.test(frame)) return 'aluminio';
            return null;
        },
        options: [
            { value: 'aluminio', label: 'Aluminio' },
            { value: 'carbono', label: 'Carbono' }
        ]
    }
];

// The options a group offers for a list of bikes: its fixed ones, or the distinct values found.
export const groupOptions = (group, bikes, ctx) => {
    const present = new Set(bikes.map((bike) => group.value(bike, ctx)).filter(Boolean));
    if (group.options) return group.options.filter((option) => present.has(option.value));
    return [...present]
        .sort(group.sort || ((a, b) => a.localeCompare(b)))
        .map((value) => ({ value, label: group.format ? group.format(value) : value }));
};

const plain = (value) => text(value).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export const matchesSearch = (bike, query, ctx) => {
    const words = plain(query).split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const haystack = plain(`raymon ${bike.modelo} ${ctx.seriesById[bike.serie_id]?.nombre} ${categoryName(bike, ctx.seriesById)} ${bike.motor_modelo}`);
    return words.every((word) => haystack.includes(word));
};
